import type React from 'react';
import { useMemo } from 'react';

import type { ColumnDef } from '@tanstack/react-table';
import type {
  DepartmentId,
  RoleId,
  Status,
  UserRecord,
  UserId,
} from '@vlabs/api/artemy/model';
import { useHasPermission, usePageAccess } from '@vlabs/permissions';
import { dateFormatter } from '@vlabs/uikit/components/table-view/cell/formatters/date';

import { BlockUserIconButton } from '@vlabs/monokit/features/point/block-user';
import { DuplicateUserIconButton } from '@vlabs/monokit/features/point/duplicate-user';
import { GoToUserIconButton } from '@vlabs/monokit/features/point/go-to-user';
import { valueOrHyphen } from '@vlabs/monokit/shared/lib/utils';
import { TableView } from '@vlabs/monokit/shared/ui/table-view';
import { TruncatedValue } from '@vlabs/monokit/shared/ui/truncated-value';

import { useUserListTableViewerTranslation } from '../i18n';
// eslint-disable-next-line import-x/no-namespace
import * as Cells from './cells';

function UserListTable({ users }: { users: UserRecord[] }): React.JSX.Element {
  const { t } = useUserListTableViewerTranslation();
  const canCreateUser = useHasPermission('users', 'create');
  const canUpdateUser = useHasPermission('users', 'update');
  const canViewUserDetails = usePageAccess('users_details');

  const columns: ColumnDef<UserRecord>[] = useMemo(() => ([
    {
      accessorKey: 'email',
      header: Cells.renderHeader(Cells.emailCell, t('table.email')),
      cell: Cells.renderCell(Cells.emailCell, (ctx) => (
        <TruncatedValue value={ctx.getValue() as string | undefined} />
      )),
    },
    {
      accessorKey: 'status',
      header: Cells.renderHeader(Cells.statusCell, t('table.status')),
      cell: Cells.renderCell(Cells.statusCell, (ctx) => (
        <Cells.StatusCell
          isSystem={ctx.row.original.is_system}
          status={ctx.getValue() as Status}
        />
      )),
    },
    {
      accessorKey: 'full_name',
      header: Cells.renderHeader(Cells.fullNameCell, t('table.full_name')),
      cell: Cells.renderCell(Cells.fullNameCell, (ctx) => (
        <TruncatedValue value={ctx.getValue() as string | undefined} />
      )),
    },
    {
      accessorKey: 'role_id',
      header: Cells.renderHeader(Cells.roleCell, t('table.role')),
      cell: Cells.renderCell(Cells.roleCell, (ctx) => (
        <Cells.RoleCell roleId={ctx.getValue() as RoleId} />
      )),
    },
    {
      accessorKey: 'last_login_at',
      header: Cells.renderHeader(Cells.lastLoginAtCell, t('table.last_login_at')),
      cell: Cells.renderCell(Cells.lastLoginAtCell, (ctx) => (
        valueOrHyphen(dateFormatter(ctx.getValue() as string | undefined))
      )),
    },
    {
      accessorKey: 'department_ids',
      header: Cells.renderHeader(Cells.departmentsCell, t('table.department')),
      cell: Cells.renderCell(Cells.departmentsCell, (ctx) => (
        <Cells.DepartmentsCell departmentIds={ctx.getValue() as DepartmentId[] | undefined} />
      )),
    },
    ...(canViewUserDetails ? [{
      accessorKey: 'user_id' as const,
      id: 'goToUser',
      header: Cells.renderHeader(Cells.actionCell),
      cell: Cells.renderCell(Cells.actionCell, (ctx) => {
        const userId = ctx.getValue() as UserId | undefined;

        return userId ? (
          <GoToUserIconButton
            title={t('actions.go_to_user')}
            userId={userId}
          />
        ) : null;
      }),
    }] : []),
    ...(canCreateUser ? [{
      accessorKey: 'user_id' as const,
      id: 'duplicateUser',
      header: Cells.renderHeader(Cells.actionCell),
      cell: Cells.renderCell(Cells.actionCell, (ctx) => {
        const userId = ctx.getValue() as UserId | undefined;

        return userId ? (
          <DuplicateUserIconButton
            title={t('actions.duplicate_user')}
            userId={userId}
          />
        ) : null;
      }),
    }] : []),
    ...(canUpdateUser ? [{
      accessorKey: 'user_id' as const,
      id: 'blockUser',
      header: Cells.renderHeader(Cells.actionCell),
      cell: Cells.renderCell(Cells.actionCell, (ctx) => {
        const userId = ctx.getValue() as UserId | undefined;

        return userId ? (
          <BlockUserIconButton
            disabled={ctx.row.original.status === 'blocked'}
            title={t('actions.block_user')}
            userId={userId}
          />
        ) : null;
      }),
    }] : []),
    { cell: Cells.dividerCell(undefined), id: 'divider' },
  ] as ColumnDef<UserRecord>[]), [t, canCreateUser, canUpdateUser, canViewUserDetails]);

  return (
    <TableView
      columns={columns}
      data={users}
    />
  );
}

export { UserListTable };
